/*
 * Copyright (c) 2026 Bolmate B.V.
 *
 * Admin panel companion to DeviceList: every e-mail address the account's
 * notifications deliver to, with how many notifications target it. Only
 * notifications whose EMAIL channel is on count — an address configured on a
 * push-only notification receives nothing. When the environment reroutes all
 * outgoing mail to the config.ini test address (every non-prod env), each row
 * is annotated as redirected so a missing mail is traceable. The backend only
 * exposes a boolean, not the test addresses, to avoid leaking them.
 * Reuses the push-devices classes so both lists share one look.
 */

import React, {useContext, useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {AwesomeIcon, ResponseData, showSnackbar} from '@dvrd/dvr-controls';

import IconWithLabel from '../icon/iconWithLabel';

import {getMailRedirectInfo, sendTestMailToRecipient} from '../../util/requestSender';

import {readNotificationChannels} from '../../util/pushChannelUtil';

import {onShowConfirmSnackbar} from '../../util/renderUtil';

import {AppContext} from '../misc/appContext';

import Notification from '../../models/notificationModel';

interface Props {
    notifications: Array<Notification>;
}

export default function EmailRecipientsList(props: Props) {
    const {t} = useTranslation();
    const {notifications} = props;
    const context = useContext(AppContext);
    const accountId = context.accountContext.account?.id ?? null;
    // Non-prod reroutes every recipient to the config.ini test address. The
    // backend only tells us WHETHER that redirect is on (not the addresses).
    const [redirectActive, setRedirectActive] = useState(false);
    // Address whose test e-mail is in flight (spins its icon, blocks a re-send).
    const [testingAddress, setTestingAddress] = useState<string | null>(null);

    function onSendTestMail(address: string) {
        if (!accountId || testingAddress) return;
        setTestingAddress(address);
        sendTestMailToRecipient({
            email_address: address,
            account_id: accountId,
            callback: (data: ResponseData) => {
                setTestingAddress(null);
                if (data.success) {
                    onShowConfirmSnackbar(t('push.testMailSent', 'Test-e-mail naar dit adres verzonden'));
                } else {
                    const code = (data as {error_code?: string}).error_code;
                    const fallback =
                        (data as {message?: string}).message ||
                        t('push.error', 'Er ging iets mis, probeer het opnieuw');
                    const msg = code ? t(`push.testErrors.${code}`, fallback) : fallback;
                    showSnackbar(msg, {backgroundColor: 'color-blue-bolmate', textColor: 'white'});
                }
            },
            // `callback` fires only on 2xx — without this a network failure /
            // non-2xx leaves the icon spinning forever and testingAddress blocks
            // every further test send until remount. Clear it and surface the error.
            errorCallback: () => {
                setTestingAddress(null);
                showSnackbar(t('push.error', 'Er ging iets mis, probeer het opnieuw'), {
                    backgroundColor: 'color-blue-bolmate',
                    textColor: 'white'
                });
            }
        });
    }

    useEffect(() => {
        getMailRedirectInfo({
            callback: (data: ResponseData) => {
                if (!data.success) return;
                setRedirectActive((data as {redirect_active?: boolean}).redirect_active === true);
            }
        });
    }, []);

    const recipients = useMemo(() => {
        const counts = new Map<string, number>();
        for (const notification of notifications) {
            if (!readNotificationChannels(notification).includes('EMAIL')) continue;
            for (const raw of notification.emailAddresses ?? []) {
                const address = (raw || '').trim();
                if (!address) continue;
                counts.set(address, (counts.get(address) ?? 0) + 1);
            }
        }
        return Array.from(counts.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    }, [notifications]);

    return (
        <div className='push-devices push-emails'>
            <div className='push-devices-header'>
                <div className='push-devices-title'>{t('push.emailRecipients', 'E-mailadressen')}</div>
            </div>
            {recipients.length === 0 ? (
                <div className='push-devices-empty'>
                    {t('push.emailRecipientsNone', 'Geen e-mailadressen geconfigureerd')}
                </div>
            ) : (
                <ul className='push-devices-list'>
                    {recipients.map(([address, count]) => (
                        <li key={address} className='push-devices-row'>
                            <div className='push-devices-meta'>
                                <span className='push-devices-platform'>
                                    <AwesomeIcon name='envelope' className='push-devices-type-icon' />
                                    <span className='push-devices-platform-text'>{address}</span>
                                    {redirectActive && (
                                        <span className='push-devices-current'>
                                            {t('push.emailRedirected', '(Wordt omgeleid)')}
                                        </span>
                                    )}
                                </span>
                                <span className='push-devices-last-seen'>
                                    {t('push.emailReceivesCount', 'Ontvangt {{count}} notificaties', {count})}
                                </span>
                            </div>
                            <div className='push-devices-actions'>
                                <IconWithLabel
                                    label={t('push.testSendMail', 'Test-e-mail naar dit adres sturen')}
                                    name='paper-plane'
                                    onClick={() => onSendTestMail(address)}
                                    iconClassName={
                                        'action btn-test' +
                                        (testingAddress === address ? ' push-card-refresh-spinning' : '')
                                    }
                                />
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
